import {
    centsToFormattedDollars,
    formatCurrencyInputOnChange,
    formatDecimalInput,
    formattedDollarsToCents,
    isLiabilityAccountType,
} from '@fruitful/fruitils';
import {
    type AccountCategory,
    type AccountSummary,
    HolisticCategory,
    type HolisticSummary,
    Provider,
} from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { Redirect, router } from 'expo-router';
import { useAtom, useAtomValue } from 'jotai';
import { capitalize } from 'lodash';
import React, { type ReactNode, useRef, useState } from 'react';
import { KeyboardAvoidingView, Platform, type TextInput } from 'react-native';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckIcon,
    CONTENT_MAX_WIDTH,
    FullScreenSpinner,
    Icon,
    isEmpty,
    isWeb,
    LabelRegular,
    RotateCcwIcon,
    SafeAreaView,
    ScreenHeader,
    ScrollView,
    SelectDropdown,
    StyledBodyMediumRegularInputNoOutline,
    TextArea,
    tokens,
    UnlinkIcon,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { getPartnerDisplayFirstName } from '../../common/utils/userUtils';
import { captureException } from '../../logger/Logger';
import useGetHolisticSummary from '../hooks/useGetHolisticSummary';
import useToggleAccountVisibility from '../hooks/useToggleAccountVisibility';
import useUpdateQuilttAccount from '../hooks/useUpdateQuilttAccount';
import useUpdateSpinwheelCreditCardAccount from '../hooks/useUpdateSpinwheelCreditCardAccount';
import useUpdateSpinwheelLoanAccount from '../hooks/useUpdateSpinwheelLoanAccount';
import {
    holisticSummaryAtom,
    selectedHolisticAccountAtom,
} from '../state/HolisticAtoms';

const getInputStyleProps = (disabled: boolean) => ({
    backgroundColor: disabled
        ? tokens.color.light.gray.gray400
        : tokens.color.light.gray.gray200,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: tokens.color.light.gray.gray600,
    borderRadius: 16,
    height: 56,
});

const EditInstitutionAccountField = ({
    label,
    input,
}: {
    label: string;
    input: ReactNode;
}) => {
    return (
        <YStack gap={8}>
            <LabelRegular color="$gray1000">{label}</LabelRegular>
            {input}
        </YStack>
    );
};

const EditInstitutionAccount = () => {
    const toast = useToastController();
    const user = useAuthedUser();

    const selected = useAtomValue(selectedHolisticAccountAtom);
    if (!selected) return <Redirect href="/" />;

    const { provider } = selected;

    // Only Quiltt and Spinwheel accounts can be edited through this component
    if (provider !== Provider.QUILTT && provider !== Provider.SPINWHEEL) {
        return <Redirect href="/" />;
    }
    const [nickname, setNickname] = useState<string>(selected.name);
    const [category, setCategory] = useState<string>(selected.category);
    const [minimumPayment, setMinimumPayment] = useState<string | null>(
        selected.minimumPayment === null
            ? ''
            : centsToFormattedDollars(selected.minimumPayment ?? 0),
    );
    const [interestRate, setInterestRate] = useState<string | undefined>(
        formatDecimalInput(selected.interestRate?.toString() ?? ''),
    );
    const [description, setDescription] = useState<string | undefined>(
        selected.description,
    );
    const [entireBalancePaidMonthly, setEntireBalancePaidMonthly] = useState<
        boolean | undefined
    >(selected.entireBalancePaidMonthly ?? undefined);

    const primaryUserId = user.id;
    const partnerUserId = user.partnerMember?.id;
    const primaryFirstName = user.firstName ?? '';
    const partnerFirstName = getPartnerDisplayFirstName(
        user.partnerMember?.firstName,
    );

    const ownerOptions: Array<{ label: string; userId: string }> = [
        { label: primaryFirstName, userId: primaryUserId },
    ];

    if (partnerUserId != null) {
        ownerOptions.push({ label: partnerFirstName, userId: partnerUserId });
    }

    const initialOwnerUserId =
        selected.userId === primaryUserId || selected.userId === partnerUserId
            ? (selected.userId ?? primaryUserId)
            : primaryUserId;
    const [selectedOwnerUserId, setSelectedOwnerUserId] =
        useState<string>(initialOwnerUserId);

    const selectedOwnerName =
        ownerOptions.find((option) => option.userId === selectedOwnerUserId)
            ?.label ?? primaryFirstName;

    const isCreditAccount = selected.type?.toLowerCase() === 'credit';

    // We need the minimum payment and interest for borrow accounts and credit accounts where the member does not pay off the entire monthly balance
    // Used to determine whether we need to show the additional fields
    const [needsMoreDetails, setNeedsMoreDetails] = useState<boolean>(
        selected.category?.toLowerCase() === 'borrow' ||
            (isCreditAccount && entireBalancePaidMonthly === false),
    );

    const [holisticSummary, setHolisticSummary] = useAtom(holisticSummaryAtom);
    const { mutate: updateQuilttAccount, isPending } = useUpdateQuilttAccount();
    const { mutateAsync: updateSpinwheelCreditCardAccount } =
        useUpdateSpinwheelCreditCardAccount();
    const { mutateAsync: updateSpinwheelLoanAccount } =
        useUpdateSpinwheelLoanAccount();

    const queryClient = useQueryClient();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { mutateAsync: toggleAccountVisibility } =
        useToggleAccountVisibility();

    const { refetch: refetchHolisticSummary } = useGetHolisticSummary(
        holisticSummary?.[category] == null,
    );

    const onSubmitHideAccount = async (
        accountId: string,
        provider: Provider,
        isLastAccountOnLink: boolean,
    ) => {
        try {
            await toggleAccountVisibility({
                body: {
                    accountId,
                    provider,
                    isHidden: true,
                },
            });

            void refetchHolisticSummary();

            toast.show('Account has been hidden', {
                duration: 4000,
                icon: (
                    <View
                        backgroundColor="$gray100"
                        padding={8}
                        borderRadius={4}
                    >
                        <Icon icon={UnlinkIcon} size={20} color="$gray1100" />
                    </View>
                ),
                ...(isLastAccountOnLink
                    ? {}
                    : {
                          button: (
                              <Button
                                  variant={ButtonVariant.PRIMARY}
                                  size={ButtonSize.SMALL}
                                  text="Undo"
                                  icon={RotateCcwIcon}
                                  onPress={() => {
                                      void handleUndoHide(accountId, provider);
                                  }}
                                  width="auto"
                              />
                          ),
                      }),
            });

            // Navigate to the appropriate holistic category screen
            // we need to filter out the account that was just hidden, because it's very likely the atom hasn't yet been updated after the holistic summary fetch
            const categoryAccounts: AccountSummary[] =
                holisticSummary?.[category as keyof HolisticSummary]
                    ?.accounts ?? [];
            const remainingAccountsInCategory = categoryAccounts.filter(
                (account) => account.id !== accountId,
            );

            if (remainingAccountsInCategory.length < 1) {
                router.navigate('/home');
            } else {
                router.navigate({
                    pathname: '/holistic/[category]',
                    params: { category },
                });
            }
        } catch (e) {
            // Still refresh in case this call partially worked
            void refetchHolisticSummary();

            captureException(e);
            toast.show('Error unlinking account', {
                duration: 2000,
            });
        }
    };

    const handleUndoHide = async (accountId: string, provider: Provider) => {
        try {
            toast.hide();
            await toggleAccountVisibility({
                body: {
                    accountId,
                    provider,
                    isHidden: false,
                },
            });
            void refetchHolisticSummary();
            toast.show('Account restored', {
                icon: (
                    <View
                        backgroundColor="$green1000"
                        padding={16}
                        borderRadius={12}
                    >
                        <Icon icon={CheckIcon} size={20} color="$white" />
                    </View>
                ),
            });
        } catch (e) {
            captureException(e);
            toast.show('Error undoing unlink account', {
                duration: 2000,
            });
        }
    };

    const onSave = () => {
        setIsSubmitting(true);
        const isLiability = isLiabilityAccountType(selected.type);
        const isSpinwheelCreditCard =
            provider === Provider.SPINWHEEL && isCreditAccount;
        const isSpinwheelLoan =
            provider === Provider.SPINWHEEL && !isSpinwheelCreditCard;

        const minimumPaymentToSend =
            provider === Provider.SPINWHEEL
                ? formattedDollarsToCents(minimumPayment ?? '') || null
                : isLiability && needsMoreDetails
                  ? formattedDollarsToCents(minimumPayment ?? '') || null
                  : null;

        const interestRateToSend =
            provider === Provider.SPINWHEEL && !isEmpty(interestRate)
                ? parseFloat(interestRate ?? '0')
                : isLiability && needsMoreDetails && !isEmpty(interestRate)
                  ? parseFloat(interestRate ?? '0')
                  : null;

        if (provider === Provider.QUILTT) {
            updateQuilttAccount(
                {
                    body: {
                        userId: user.id,
                        remoteId: selected.id,
                        name: nickname,
                        holisticCategory:
                            category.toUpperCase() as AccountCategory,
                        minimumPayment: minimumPaymentToSend,
                        interestRate: interestRateToSend,
                        description,
                        entireBalancePaidMonthly,
                    },
                },
                {
                    onSuccess: async (updatedAccount: AccountSummary) => {
                        await queryClient.invalidateQueries({
                            queryKey: [`getAccountOverviewSummary/${user.id}`],
                        });

                        if (holisticSummary) {
                            setHolisticSummary(
                                Object.fromEntries(
                                    Object.entries(holisticSummary).map(
                                        ([category, group]) => [
                                            category,
                                            {
                                                ...group,
                                                accounts: group.accounts.map(
                                                    (account) =>
                                                        account.id ===
                                                        updatedAccount.id
                                                            ? updatedAccount
                                                            : account,
                                                ),
                                            },
                                        ],
                                    ),
                                ) as HolisticSummary,
                            );
                        }

                        setIsSubmitting(false);
                        toast.show('Updated', {
                            icon: (
                                <View
                                    backgroundColor="$green1000"
                                    padding={16}
                                    borderRadius={12}
                                >
                                    <Icon
                                        icon={CheckIcon}
                                        size={20}
                                        color="$white"
                                    />
                                </View>
                            ),
                            description: `${updatedAccount.name ?? 'Account'} updated`,
                        });

                        const originalCategory =
                            selected.category?.toLowerCase();
                        if (originalCategory != null) {
                            router.replace({
                                pathname: '/holistic/[category]',
                                params: { category: originalCategory },
                            });
                        } else {
                            router.back();
                        }
                    },
                    onError: (error: Error) => {
                        setIsSubmitting(false);
                        toast.show('Could not update account');
                        captureException(error);
                    },
                },
            );
        } else {
            const holisticCategoryValue =
                category.toUpperCase() as HolisticCategory;

            const validCategories = Object.values(HolisticCategory);
            if (!validCategories.includes(holisticCategoryValue)) {
                toast.show(`Invalid category: ${category}`);
                setIsSubmitting(false);
                return;
            }

            const updateBody: {
                id?: string;
                displayName?: string;
                userId?: string;
                holisticCategory?: HolisticCategory;
                minimumPaymentAmountCents?: number;
                interestRate?: number | null;
                description?: string;
            } = {
                id: selected.id,
                displayName: nickname,
            };

            if (
                selectedOwnerUserId &&
                selectedOwnerUserId !== selected.userId
            ) {
                updateBody.userId = selectedOwnerUserId;
            }

            if (holisticCategoryValue != null) {
                updateBody.holisticCategory = holisticCategoryValue;
            }

            if (minimumPaymentToSend != null && minimumPaymentToSend > 0) {
                updateBody.minimumPaymentAmountCents = minimumPaymentToSend;
            }

            if (
                interestRateToSend != null &&
                !isNaN(interestRateToSend) &&
                interestRateToSend > 0
            ) {
                updateBody.interestRate = interestRateToSend;
            }

            updateBody.description = description ?? '';

            const updateSuccessCallback = async (
                updatedAccount: AccountSummary,
            ) => {
                await queryClient.invalidateQueries({
                    queryKey: [`getAccountOverviewSummary/${user.id}`],
                });

                if (holisticSummary) {
                    setHolisticSummary(
                        Object.fromEntries(
                            Object.entries(holisticSummary).map(
                                ([category, group]) => [
                                    category,
                                    {
                                        ...group,
                                        accounts: group.accounts.map(
                                            (account) =>
                                                account.id === updatedAccount.id
                                                    ? updatedAccount
                                                    : account,
                                        ),
                                    },
                                ],
                            ),
                        ) as HolisticSummary,
                    );
                }

                setIsSubmitting(false);
                toast.show('Updated', {
                    icon: (
                        <View
                            backgroundColor="$green1000"
                            padding={16}
                            borderRadius={12}
                        >
                            <Icon icon={CheckIcon} size={20} color="$white" />
                        </View>
                    ),
                    description: `${updatedAccount.name ?? 'Account'} updated`,
                });

                const originalCategory = selected.category?.toLowerCase();
                if (originalCategory != null) {
                    router.replace({
                        pathname: '/holistic/[category]',
                        params: { category: originalCategory },
                    });
                } else {
                    router.back();
                }
            };

            const updateErrorCallback = (error: Error) => {
                setIsSubmitting(false);
                toast.show('Could not update Account');
                captureException(error);
            };

            if (isSpinwheelLoan) {
                void updateSpinwheelLoanAccount(
                    {
                        body: updateBody,
                    },
                    {
                        onSuccess: updateSuccessCallback,
                        onError: updateErrorCallback,
                    },
                );
            } else {
                void updateSpinwheelCreditCardAccount(
                    {
                        body: updateBody,
                    },
                    {
                        onSuccess: updateSuccessCallback,
                        onError: updateErrorCallback,
                    },
                );
            }
        }
    };

    const entireBalancePaidMonthlyValue = () => {
        if (provider !== Provider.SPINWHEEL) {
            return entireBalancePaidMonthly === true
                ? 'Yes'
                : entireBalancePaidMonthly === false
                  ? 'No'
                  : '';
        } else if (
            selected.accountRating != null &&
            selected.accountRating !== ''
        ) {
            if (
                selected.accountRating === '0' ||
                selected.accountRating === '1'
            ) {
                return 'Yes';
            } else {
                return 'No';
            }
        }
        return '';
    };

    const interestRateInput = useRef<TextInput | null>(null);
    const [interestRateWidth, setInterestRateWidth] = useState(0);

    const sanitizedMinimumPayment = minimumPayment?.replace(/[^0-9.]/g, '');

    const isMissingRequiredDetails =
        isCreditAccount &&
        needsMoreDetails &&
        (isEmpty(sanitizedMinimumPayment) || isEmpty(interestRate));

    if (isSubmitting) {
        return <FullScreenSpinner />;
    }

    return (
        <SafeAreaView style={{ flex: 1 }} edges={['left', 'bottom', 'right']}>
            <YStack
                flex={1}
                alignItems="center"
                width="100%"
                maxWidth={isWeb ? CONTENT_MAX_WIDTH : '100%'}
                alignSelf="center"
            >
                <ScreenHeader
                    useSafeAreaInsetTop={false}
                    title="Edit account"
                    subtitle={`${selected.name} ${selected.accountNumberMasked != null ? ` *${selected.accountNumberMasked}` : ''}`}
                    hasBackButton={isWeb}
                    hasCloseButton={!isWeb}
                />
                <KeyboardAvoidingView
                    style={{
                        flex: 1,
                        height: '100%',
                        width: '100%',
                        ...(isWeb && {
                            justifyContent: 'center',
                            alignItems: 'center',
                        }),
                    }}
                    behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                    keyboardVerticalOffset={0}
                >
                    <ScrollView
                        showsVerticalScrollIndicator={isWeb}
                        flex={1}
                        width="100%"
                        contentContainerStyle={{
                            flexGrow: 1,
                            width: '100%',
                            paddingHorizontal: 24,
                            paddingTop: 24,
                            paddingBottom: isWeb ? 24 : 0,
                        }}
                        automaticallyAdjustKeyboardInsets={true}
                    >
                        <YStack gap={24} paddingTop={16} width="100%" flex={1}>
                            <EditInstitutionAccountField
                                label="Account nickname (optional)"
                                input={
                                    <StyledBodyMediumRegularInputNoOutline
                                        value={nickname}
                                        onChangeText={(v) => {
                                            setNickname(v);
                                        }}
                                        {...getInputStyleProps(false)}
                                    />
                                }
                            />
                            {ownerOptions.length > 1 &&
                                provider === Provider.SPINWHEEL && (
                                    <EditInstitutionAccountField
                                        label="Account owner"
                                        input={
                                            <SelectDropdown
                                                defaultValue={capitalize(
                                                    selectedOwnerName,
                                                )}
                                                options={ownerOptions.map(
                                                    (option) => option.label,
                                                )}
                                                onSelect={(selectedName) => {
                                                    const selectedOption =
                                                        ownerOptions.find(
                                                            (option) =>
                                                                option.label ===
                                                                selectedName,
                                                        );
                                                    if (selectedOption) {
                                                        setSelectedOwnerUserId(
                                                            selectedOption.userId,
                                                        );
                                                    }
                                                }}
                                                buttonStyle={{
                                                    ...getInputStyleProps(
                                                        false,
                                                    ),
                                                }}
                                                buttonTextStyle={{
                                                    fontWeight: '500',
                                                }}
                                                rowItemTextStyle={{
                                                    fontWeight: '500',
                                                }}
                                            />
                                        }
                                    />
                                )}
                            <EditInstitutionAccountField
                                label="Category"
                                input={
                                    <SelectDropdown
                                        defaultValue={capitalize(category)}
                                        options={[
                                            'Spend',
                                            'Save',
                                            'Invest',
                                            'Borrow',
                                        ]}
                                        onSelect={(category) => {
                                            setCategory(category.toLowerCase());
                                        }}
                                        buttonStyle={{
                                            ...getInputStyleProps(false),
                                        }}
                                        buttonTextStyle={{
                                            fontWeight: '500',
                                        }}
                                        rowItemTextStyle={{
                                            fontWeight: '500',
                                        }}
                                    />
                                }
                            />
                            {isCreditAccount && (
                                <EditInstitutionAccountField
                                    label="Do you pay the whole balance off each month?"
                                    input={
                                        <SelectDropdown
                                            options={['Yes', 'No']}
                                            defaultValue={entireBalancePaidMonthlyValue()}
                                            disabled={
                                                provider === Provider.SPINWHEEL
                                            }
                                            onSelect={(value) => {
                                                // If the member does not pay off the entire balance monthly, we need more details
                                                setNeedsMoreDetails(
                                                    value === 'No',
                                                );
                                                setEntireBalancePaidMonthly(
                                                    value === 'Yes',
                                                );
                                            }}
                                            buttonStyle={{
                                                ...getInputStyleProps(
                                                    provider ===
                                                        Provider.SPINWHEEL,
                                                ),
                                            }}
                                            buttonTextStyle={{
                                                fontWeight: '500',
                                            }}
                                            rowItemTextStyle={{
                                                fontWeight: '500',
                                            }}
                                        />
                                    }
                                />
                            )}
                            {((isLiabilityAccountType(selected.type) &&
                                (!isCreditAccount || needsMoreDetails)) ||
                                provider === Provider.SPINWHEEL) && (
                                <>
                                    <EditInstitutionAccountField
                                        label="Minimum payment"
                                        input={
                                            <StyledBodyMediumRegularInputNoOutline
                                                {...getInputStyleProps(false)}
                                                keyboardType="decimal-pad"
                                                value={minimumPayment ?? '0'}
                                                editable={true}
                                                onChangeText={(v) => {
                                                    setMinimumPayment(
                                                        formatCurrencyInputOnChange(
                                                            minimumPayment ??
                                                                '',
                                                            v,
                                                        ),
                                                    );
                                                }}
                                            />
                                        }
                                    />

                                    <EditInstitutionAccountField
                                        label="Interest rate"
                                        input={
                                            <XStack
                                                alignItems="center"
                                                justifyContent="flex-start"
                                                {...getInputStyleProps(false)}
                                                onPress={() =>
                                                    interestRateInput.current?.focus()
                                                }
                                            >
                                                {/* hidden element to help determine input width so it only stretches as far as its content */}
                                                <View
                                                    position="absolute"
                                                    opacity={0}
                                                    onLayout={(e) => {
                                                        setInterestRateWidth(
                                                            e.nativeEvent.layout
                                                                .width + 1,
                                                        );
                                                    }}
                                                >
                                                    <BodyMediumRegular>
                                                        {interestRate}
                                                    </BodyMediumRegular>
                                                </View>
                                                <StyledBodyMediumRegularInputNoOutline
                                                    ref={interestRateInput}
                                                    keyboardType="decimal-pad"
                                                    width={interestRateWidth}
                                                    value={(
                                                        interestRate ?? ''
                                                    ).toString()}
                                                    editable={true}
                                                    onChangeText={(v) => {
                                                        setInterestRate(
                                                            formatDecimalInput(
                                                                v,
                                                            ),
                                                        );
                                                    }}
                                                />
                                                <BodyMediumRegular>
                                                    %
                                                </BodyMediumRegular>
                                            </XStack>
                                        }
                                    />
                                </>
                            )}
                            <EditInstitutionAccountField
                                label="Description (optional)"
                                input={
                                    <TextArea
                                        value={description}
                                        onChangeText={(v) => {
                                            setDescription(v);
                                        }}
                                        numberOfLines={5}
                                        multiline={true}
                                        scrollEnabled={false}
                                        fontWeight="500"
                                        paddingBottom={16}
                                        focusVisibleStyle={{
                                            outlineWidth: 0,
                                            borderWidth: 0,
                                        }}
                                        {...getInputStyleProps(false)}
                                        height={132}
                                    />
                                }
                            />
                        </YStack>
                        <View paddingTop={16} gap={8}>
                            <Button
                                variant={ButtonVariant.TERTIARY}
                                tone="danger"
                                text="Hide account"
                                onPress={async () => {
                                    await onSubmitHideAccount(
                                        selected.id,
                                        provider,
                                        selected?.isLastAccountOnLink ?? false,
                                    );
                                }}
                                disabled={isPending || isMissingRequiredDetails}
                            />
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                text="Save"
                                onPress={onSave}
                                disabled={isPending || isMissingRequiredDetails}
                            />
                        </View>
                    </ScrollView>
                </KeyboardAvoidingView>
            </YStack>
        </SafeAreaView>
    );
};

export default EditInstitutionAccount;
